import { AgmCoreModule, LazyMapsAPILoaderConfigLiteral, LAZY_MAPS_API_CONFIG } from '@agm/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoggerModule } from 'ngx-logger';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { CarouselModule } from 'primeng/carousel';
import { GalleriaModule } from 'primeng/galleria';
import { environment } from 'src/main/webapp/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChangelogComponent } from './changelog/changelog.component';
import { ErrorComponent } from './error/error.component';
import { ActivitiesComponent } from './home/activities/activities.component';
import { CarouselImagesComponent } from './home/carousel-images/carousel-images.component';
import { FooterComponent } from './home/footer/footer.component';
import { HeaderComponent } from './home/header/header.component';
import { AgencyInfoComponent } from './home/header/agency-info/agency-info.component';
import { HomeComponent } from './home/home.component';
import { CityComponent } from './home/itinerary/city/city.component';
import { PnrComponent } from './home/itinerary/common/pnr/pnr.component';
import { FlightSegmentDetailComponent } from './home/itinerary/flights/flight-segment-detail/flight-segment-detail.component';
import { FlightStopoverComponent } from './home/itinerary/flights/flight-stopover/flight-stopover.component';
import { FlightsComponent } from './home/itinerary/flights/flights.component';
import { HotelsComponent } from './home/itinerary/hotels/hotels.component';
import { RoomComponent } from './home/itinerary/hotels/room/room.component';
import { AlternativesComponent } from './home/itinerary/hotels/alternatives/alternatives.component';
import { ItineraryComponent } from './home/itinerary/itinerary.component';
import { OptionalsComponent } from './home/itinerary/optionals/optionals.component';
import { TransfersComponent } from './home/itinerary/transfers/transfers.component';
import { VehicleDetailsComponent } from './home/itinerary/vehicles/vehicle-details/vehicle-details.component';
import { VehiclesComponent } from './home/itinerary/vehicles/vehicles.component';
import { MapComponent } from './home/map/map.component';
import { BlockImageRecapComponent } from './home/overview/block-image-recap/block-image-recap.component';
import { BlockImageComponent } from './home/overview/block-image/block-image.component';
import { OverviewComponent } from './home/overview/overview.component';
import { RecapCityComponent } from './home/overview/recap/recap-city/recap-city.component';
import { RecapEscortedsComponent } from './home/overview/recap/recap-escorteds/recap-escorteds.component';
import { RecapFlightsComponent } from './home/overview/recap/recap-flights/recap-flights.component';
import { RecapHotelsComponent } from './home/overview/recap/recap-hotels/recap-hotels.component';
import { RecapOptionalComponent } from './home/overview/recap/recap-optional/recap-optional.component';
import { RecapTransfersComponent } from './home/overview/recap/recap-transfers/recap-transfers.component';
import { RecapVehiclesComponent } from './home/overview/recap/recap-vehicles/recap-vehicles.component';
import { RecapComponent } from './home/overview/recap/recap.component';
import { RentACarComponent } from './home/rent-a-car/rent-a-car.component';
import { SistemazioniComponent } from './home/sistemazioni/sistemazioni.component';
import { TransportComponent } from './home/transport/transport.component';
import { ChangePasswordComponent } from './options/change-password/change-password.component';
import {
  ForgottenPasswordComponent
} from './options/forgotten-password/forgotten-password.component';
import { LanguageSwitcherComponent } from './options/language-switcher/language-switcher.component';
import { LoginComponent } from './options/login/login.component';
import { LogoutComponent } from './options/logout/logout.component';
import { ResetPasswordComponent } from './options/reset-password/reset-password.component';
import { AddPermissionComponent } from './options/share-itinerary/add-permission/add-permission.component';
import { DeletePermissionComponent } from './options/share-itinerary/delete-permission/delete-permission.component';
import { EditPermissionComponent } from './options/share-itinerary/edit-permission/edit-permission.component';
import { ShareItineraryComponent } from './options/share-itinerary/share-itinerary.component';
import { LocalizedDatePipe } from './pipe/localized-date.pipe';
import { LocalizedCurrencyPipe } from './pipe/localized-currency.pipe';
import { SafeHtmlPipe } from './pipe/safe-html.pipe';
import { RestTmtService } from './rest-tmt.service';
import { LoggingErrorHandler } from './services/error/logging-error-handler';
import { SummaryComponent } from './summary/summary.component';

import { registerLocaleData, CurrencyPipe } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { DocumentsComponent } from './documents/documents.component';
import { EscortedComponent } from './home/escorted/escorted.component';
import { TermsComponent } from './home/terms/terms.component';
import { ExtraComponent } from './home/extra/extra.component';
import { NotesComponent } from './home/notes/notes.component';
import { FallbackImageDirective } from './shared/fallback-image.directive';
import { AppServiceIconComponent } from './shared/service-icon/service-icon.component';
import { PrintService } from './print.service';

import { PrintEcComponent } from './home/print-ec/print-ec.component';
import { PrintComponent } from './home/print/print.component';
import { SharedPrintComponent } from './shared-print/shared-print.component';
import { EmailModalComponent } from './home/print/email-modal/email-modal.component';
import { PrintIconComponent } from './home/print/print-icon/print-icon.component';
import { PwaInstallBannerComponent } from './components/pwa-install-banner/pwa-install-banner.component';
import { AuditComponent } from './audit/audit.component';

// Registra i dati locali per la lingua olandese
registerLocaleData(localeNl);

// ngx-translate
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function agmConfigFactory(service: RestTmtService, config: LazyMapsAPILoaderConfigLiteral) {
  return () => config.apiKey = service.getClient().GoogleMapKey;
}

/**ToDo Documentazione */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    AuditComponent,
    HomeComponent,
    MapComponent,
    ItineraryComponent,
    OverviewComponent,
    FooterComponent,
    HeaderComponent,
    AgencyInfoComponent,
    CityComponent,
    VehiclesComponent,
    TransfersComponent,
    HotelsComponent,
    OptionalsComponent,
    FlightsComponent,
    BlockImageRecapComponent,
    BlockImageComponent,
    RecapComponent,
    RecapFlightsComponent,
    RecapCityComponent,
    RecapVehiclesComponent,
    RecapTransfersComponent,
    RecapHotelsComponent,
    RecapOptionalComponent,
    RecapEscortedsComponent,
    LanguageSwitcherComponent,
    LocalizedDatePipe,
    LocalizedCurrencyPipe,
    FlightStopoverComponent,
    FlightSegmentDetailComponent,
    VehicleDetailsComponent,
    SistemazioniComponent,
    RentACarComponent,
    RoomComponent,
    AlternativesComponent,
    TransportComponent,
    PnrComponent,
    ErrorComponent,
    ChangelogComponent,
    ActivitiesComponent,
    SafeHtmlPipe,
    CarouselImagesComponent,
    ShareItineraryComponent,
    LoginComponent,
    ResetPasswordComponent,
    ForgottenPasswordComponent,
    ChangePasswordComponent,
    AddPermissionComponent,
    DeletePermissionComponent,
    EditPermissionComponent,
    LogoutComponent,
    SummaryComponent,
    DocumentsComponent,
    EscortedComponent,
    TermsComponent,
    ExtraComponent,
    NotesComponent,
    PrintComponent,
    PrintEcComponent,
    SharedPrintComponent,
    EmailModalComponent,
    PrintIconComponent,
    PwaInstallBannerComponent,
    FallbackImageDirective,
    AppServiceIconComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    // ngx-translate
    TranslateModule.forRoot({
      loader: {
        deps: [HttpClient],
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory
      }
    }),

    //loader
    NgxUiLoaderModule,
    //da rimettere se si vorrà usare un loader per qualsiasi richiesta HTTP
    //NgxUiLoaderHttpModule.forRoot({ showForeground: true }),

    // logging
    LoggerModule.forRoot({
      httpResponseType: 'text', // perché logstash restituisce "ok" in plaintext ogni riceve un log
      level: environment.logging.consoleLogLevel,
      serverLogLevel: environment.logging.serverLogLevel,
      serverLoggingUrl: environment.logging.serverUrl
    }),

    AgmCoreModule.forRoot({
      // please get your own API key here:
      // https://developers.google.com/maps/documentation/javascript/get-api-key?hl=en
      apiKey: 'initialKey',
      language: navigator.language || 'en'
    }),

    NgbModule,
    CarouselModule,
    GalleriaModule
  ],

  providers: [
    RestTmtService,
    CurrencyPipe,
    LocalizedCurrencyPipe,
    PrintService,
    // ngx-translate (propaga la lingua scelta anche alle pipe di Angular)
    {
      deps: [TranslateService],
      provide: LOCALE_ID,
      useFactory: (ngxTranslateService: { currentLang: {} }) => ngxTranslateService.currentLang
    },

    // google maps init
    {
      provide: APP_INITIALIZER,
      useFactory: agmConfigFactory,
      deps: [RestTmtService, LAZY_MAPS_API_CONFIG],
      multi: true
    },

    // logging (logga anche le eccezioni generiche su elk)
    {
      provide: ErrorHandler,
      useClass: LoggingErrorHandler
    },
    {
      provide: LOCALE_ID,
      useValue: 'it-IT'
    },
  ],
  entryComponents: [
    PrintComponent,
    PrintEcComponent,
    EmailModalComponent
  ]
})
export class AppModule { }
